import type { JSX, ReactNode } from 'react';
import Markdown from 'react-markdown';

/**
 * Tia's words in every place they are shown — the Studio column, the table's AI window, a player's chat, the answer
 * to @Tia in the shared chat (owner, 23.10: raw `**bold**` and `- lists` stood in the chat). Markdown, safely: raw HTML
 * in the text is dropped (not rendered, not shown as tags), a link goes out only to http(s) / mailto and opens in a new
 * tab, a picture by a link is not fetched (only its text stays). Look: mockups studio-ai-chat-preview-v2.html
 * (`.msg.ai p`) and tia-stage-preview.html — short paragraphs with a small gap, bold for the gist, compact lists;
 * styles `.tia-md` in styles/index.css, colours from the surface (it inherits the text colour).
 */

const SAFE_URL = /^(https?:|mailto:)/i;

/** Only links that leave for a web page or a mail; anything else (javascript:, data:, relative paths) is dropped. */
export function safeUrl(url: string): string {
  const u = url.trim();
  return SAFE_URL.test(u) ? u : '';
}

function Link({ href, children }: { href?: string | undefined; children?: ReactNode }): JSX.Element {
  if (href === undefined || href === '') return <span>{children}</span>;
  return <a href={href} target="_blank" rel="noopener noreferrer nofollow">{children}</a>;
}

export interface TiaMarkdownProps {
  readonly text: string;
  readonly className?: string;
  /** In a line after the author's name (the shared chat): the first paragraph continues the line. */
  readonly inline?: boolean;
}

export function TiaMarkdown({ text, className, inline }: TiaMarkdownProps): JSX.Element {
  return (
    <div className={`tia-md${inline === true ? ' tia-md--inline' : ''}${className ? ` ${className}` : ''}`} data-testid="tia-md">
      <Markdown
        skipHtml
        urlTransform={safeUrl}
        disallowedElements={['img']}
        unwrapDisallowed
        components={{ a: Link }}
      >
        {text}
      </Markdown>
    </div>
  );
}
